import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { Link } from "@/lib/router-compat";

const seo = {
  title: "Medications After TMS | Neuro Wellness TMS",
  description:
    "Do you need to keep taking medication after TMS? Learn about medication considerations following TMS and SAINT treatment.",
  canonical: "/medications-after-tms",
  keywords:
    "medications after TMS, TMS and antidepressants, continuing medication after TMS, SAINT medication after treatment",
};

const relatedLinks = [
  { title: "Maintenance and Relapse", href: "/maintenance-and-relapse" },
  { title: "What Is TMS?", href: "/contents/tms/what-is-tms-therapy" },
  { title: "Compare Treatments", href: "/compare-treatments" },
  { title: "Current Patients", href: "/current-patients" },
];

export default function MedicationsAfterTms() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="After Treatment"
      title="Medications After TMS"
      subtitle="Whether you continue, adjust, or stop medication after TMS is a clinical decision made with your provider. Here's what to know."
    >
      <DecisionSection heading="TMS doesn't replace medication automatically">
        <p>
          TMS is not necessarily a replacement for medication. Many people
          continue taking their current antidepressants during and after TMS
          treatment. Whether you continue, adjust, or taper off medication is a
          decision made between you and your prescriber — not something a
          website can determine.
        </p>
      </DecisionSection>

      <DecisionSection heading="What the research suggests">
        <p>
          TMS is often used alongside medication, not instead of it. Some people
          find that after TMS, their current medication works better than it did
          before. Others work with their prescriber to reduce or discontinue
          medication. There's no single answer — it depends on your response to
          treatment and your clinical situation.
        </p>
      </DecisionSection>

      <DecisionSection heading="What to discuss with your prescriber">
        <DecisionList
          items={[
            "Whether to continue your current medication during TMS",
            "How your response to TMS may affect your medication plan",
            "Whether any adjustments should be made after treatment",
            "The timeline for any medication changes",
            "Signs to watch for that may indicate a need to adjust",
          ]}
        />
      </DecisionSection>

      <DecisionSection heading="For SAINT specifically">
        <p>
          SAINT is a five-day protocol. Your medication decisions after SAINT
          are made with your prescriber based on your response. Some patients
          continue their medication; others make adjustments. This is always a
          clinical decision, never one made from a webpage.
        </p>
      </DecisionSection>

      <DecisionSection heading="Your next step">
        <p>
          Call{" "}
          <a
            href="tel:9548279793"
            className="text-accent font-semibold hover:underline"
          >
            (954) 827-9793
          </a>{" "}
          or{" "}
          <Link to="/contactus" className="text-accent hover:underline">
            schedule a consultation
          </Link>
          .
        </p>
      </DecisionSection>

      <InlineRelatedLinks heading="Related Pages" links={relatedLinks} />
    </DecisionPageLayout>
  );
}
